import { useState, useEffect } from "react";
import styles from "./Addresses.module.scss";
import Loading from "../../../components/common/loading/Loading";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import AccountSidebar from "../../../components/common/account-sidebar/AccountSidebar";
import { useTranslation } from "react-i18next";
import Input from "../../../components/common/input/Input";
import Button from "../../../components/common/button/Button";
import MapLocationPicker from "../../../components/common/map-location-picker/MapLocationPicker";
import Select from "../../../components/common/select/Select";
import LocationService from "../../../api/services/locations.service";
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
import { toast } from "sonner";

interface Address {
  id: number;
  user_id: number;
  city_id: number;
  city: {
    id: number;
    name: string;
    country_id: number;
    country: {
      id: number;
      name: string;
      created: string;
    };
    created: string;
  };
  zip_code: string;
  address: string;
  phone: string;
  latitude?: number;
  longitude?: number;
  created: string;
}

interface City {
  id: number;
  name: string;
}

interface Country {
  id: number;
  name: string;
}

const Addresses = () => {
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [countryId, setCountryId] = useState<number | null>(null);
  const [cityId, setCityId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    country: "",
    city: "",
    address: "",
    phone: "",
    zip_code: "",
    latitude: 40.4093 as number | undefined,
    longitude: 49.8671 as number | undefined,
  });
  const [mapCenter, setMapCenter] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const { t } = useTranslation("translation");

  const { data: countries } = useInfiniteScroll<Country>({
    fetchFn: async (page) => {
      const res = await LocationService.countries(page, 20);
      return res?.data?.data;
    },
    pageSize: 20,
  });

  const { data: cities } = useInfiniteScroll<City>({
    fetchFn: async (page) => {
      if (!countryId) return [];
      const res = await LocationService.cities(Number(countryId), page, 20);
      return res?.data?.data;
    },
    pageSize: 20,
    enabled: Boolean(countryId),
    deps: [countryId],
  });

  useEffect(() => {
    setLoading(true);
    const getData = async () => {
      try {
        const res = await LocationService.getAll(1, 100);
        setAddresses(res.data?.data || []);
      } catch (err) {
        console.error(err);
        toast.error(t("common_error") || "Xəta baş verdi");
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [t]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (value: { id: string; name: string }) => {
    const selectedCountryId = Number(value.id);
    setCountryId(selectedCountryId);
    setCityId(null);
    setFormData((prev) => ({ ...prev, country: value.name, city: "" }));
    setMapCenter(null);
  };

  const handleCityChange = (value: { id: string; name: string }) => {
    const selectedCityId = Number(value.id);
    setCityId(selectedCityId);
    setFormData((prev) => ({ ...prev, city: value.name }));

    // Şəhər seçildikdə default koordinatları təyin et
    if (selectedCityId) {
      setMapCenter({ latitude: 40.4093, longitude: 49.8671 });
      setFormData((prev) => ({
        ...prev,
        latitude: 40.4093,
        longitude: 49.8671,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!cityId) {
      toast.error(t("my_locations.errors.cityRequired"));
      return;
    }

    setLoading(true);
    try {
      const formDataToSend = new FormData();

      formDataToSend.append("city_id", String(cityId));
      formDataToSend.append("address", formData.address);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("zip_code", formData.zip_code);

      if (editingAddress) {
        await LocationService.update(editingAddress.id, formDataToSend);
        toast.success(t("my_locations.messages.edit_success"));
      } else {
        await LocationService.create(formDataToSend);
        toast.success(t("my_locations.messages.add_success"));
      }

      // Ünvanları yenidən yüklə
      const res = await LocationService.getAll(1, 100);
      setAddresses(res.data?.data || []);

      // Formu bağla və sıfırla
      setShowForm(false);
      setEditingAddress(null);
      setCountryId(null);
      setCityId(null);
      setMapCenter(null);
      setFormData({
        country: "",
        city: "",
        address: "",
        phone: "",
        zip_code: "",
        latitude: 40.4093,
        longitude: 49.8671,
      });
    } catch (error: any) {
      console.error(error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t("common_error") ||
        "Xəta baş verdi";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (address: Address) => {
    setEditingAddress(address);

    // Set country and city IDs from nested objects
    if (address.city?.country_id) {
      setCountryId(address.city.country_id);
    }
    if (address.city_id) {
      setCityId(address.city_id);
    }

    setFormData({
      country: address.city?.country?.name || "",
      city: address.city?.name || "",
      address: address.address,
      phone: address.phone,
      zip_code: address.zip_code,
      latitude: address.latitude || 40.4093,
      longitude: address.longitude || 49.8671,
    });

    // Set map center for editing
    if (address.latitude && address.longitude) {
      setMapCenter({
        latitude: address.latitude,
        longitude: address.longitude,
      });
    }

    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(String(t("my_locations.confirm_delete")))) {
      return;
    }

    setLoading(true);
    try {
      await LocationService.delete(id);
      toast.success(t("my_locations.messages.delete_success"));

      // Ünvanları yenidən yüklə
      const res = await LocationService.getAll(1, 100);
      setAddresses(res.data?.data || []);
    } catch (error: any) {
      console.error(error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t("common_error") ||
        "Xəta baş verdi";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingAddress(null);
    setCountryId(null);
    setCityId(null);
    setMapCenter(null);
    setFormData({
      country: "",
      city: "",
      address: "",
      phone: "",
      zip_code: "",
      latitude: 40.4093,
      longitude: 49.8671,
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingAddress(null);
    setCountryId(null);
    setCityId(null);
    setMapCenter(null);
    setFormData({
      country: "",
      city: "",
      address: "",
      phone: "",
      zip_code: "",
      latitude: 40.4093,
      longitude: 49.8671,
    });
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className={styles.addresses}>
      <TitleUpdater title={t("my_locations.title")} />
      <div className="container">
        <div className={styles.wrapper}>
          <AccountSidebar />
          <div className={styles.content}>
            <h1 className={styles.pageTitle}>{t("my_locations.title")}</h1>

            {/* Boş state - heç ünvan yoxdur */}
            {addresses.length === 0 && !showForm && (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <svg
                    width="120"
                    height="120"
                    viewBox="0 0 120 120"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="60" cy="60" r="60" fill="#E0F2FE" />
                    <path
                      d="M60 30C48.954 30 40 38.954 40 50C40 65 60 85 60 85C60 85 80 65 80 50C80 38.954 71.046 30 60 30ZM60 57C56.134 57 53 53.866 53 50C53 46.134 56.134 43 60 43C63.866 43 67 46.134 67 50C67 53.866 63.866 57 60 57Z"
                      fill="#0EA5E9"
                    />
                  </svg>
                </div>
                <h2 className={styles.emptyTitle}>
                  {t("my_locations.no_address_yet")}
                </h2>
                <p className={styles.emptyText}>
                  {t("my_locations.add_address_hint")}
                </p>
                <button
                  className={styles.addButton}
                  onClick={handleAddNew}
                  disabled={loading}
                >
                  {t("my_locations.add_new_address")}
                </button>
              </div>
            )}

            {/* Form state */}
            {showForm && (
              <div className={styles.formContainer}>
                <div className={styles.formHeader}>
                  <button className={styles.backButton} onClick={handleCancel}>
                    ← {t("my_locations.back_to_addresses")}
                  </button>
                </div>
                <h2 className={styles.formTitle}>
                  {editingAddress
                    ? t("my_locations.modals.edit_title")
                    : t("my_locations.modals.add_title")}
                </h2>
                <p className={styles.formSubtitle}>
                  {t("my_locations.form_subtitle")}
                </p>
                <form className={styles.form} onSubmit={handleSubmit}>
                  <div className={styles.formGrid}>
                    <Select
                      label={String(t("my_locations.form.country"))}
                      options={countries?.map((country) => ({
                        id: String(country.id),
                        name: country.name,
                      }))}
                      value={countryId?.toString() || ""}
                      onChange={handleCountryChange}
                      placeholder={String(
                        t("my_locations.form.select_country"),
                      )}
                      disabled={loading}
                    />
                    <Select
                      label={String(t("my_locations.form.city"))}
                      options={cities?.map((city) => ({
                        id: String(city.id),
                        name: city.name,
                      }))}
                      value={cityId?.toString() || ""}
                      onChange={handleCityChange}
                      disabled={!countryId || loading}
                      placeholder={String(t("my_locations.form.select_city"))}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <Input
                      label={String(t("my_locations.form.address"))}
                      placeholder={String(
                        t("my_locations.form.address_placeholder_full"),
                      )}
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className={styles.formGrid}>
                    <Input
                      label={String(t("my_locations.form.phone_number"))}
                      placeholder="+994 50 xxx xx xx"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      phone
                      required
                      disabled={loading}
                    />
                    <Input
                      label={String(t("my_locations.form.zip_code"))}
                      placeholder={String(
                        t("my_locations.form.placeholders.zip_code"),
                      )}
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                  </div>

                  {/* Map Container */}
                  {countryId && cityId && (
                    <>
                      <div className={styles.mapContainer}>
                        <MapLocationPicker
                          latitude={
                            formData.latitude || mapCenter?.latitude || 40.4093
                          }
                          longitude={
                            formData.longitude ||
                            mapCenter?.longitude ||
                            49.8671
                          }
                          address={formData.address || ""}
                          cityName={formData.city || null}
                          countryName={formData.country || null}
                          onLocationChange={(lat, lng, address) => {
                            setFormData((prev) => ({
                              ...prev,
                              latitude: lat,
                              longitude: lng,
                              address: address || prev.address,
                            }));
                          }}
                          disabled={false}
                        />
                      </div>
                    </>
                  )}

                  {/* Selected Location Info */}
                  {countryId &&
                    cityId &&
                    formData.latitude &&
                    formData.longitude && (
                      <div className={styles.selectedLocationInfo}>
                        <p className={styles.selectedLocationLabel}>
                          {t("map.selected_location")}
                        </p>
                        {formData.address && (
                          <p className={styles.selectedLocationAddress}>
                            {formData.address}
                          </p>
                        )}
                        <p className={styles.selectedLocationCoords}>
                          {formData.latitude.toFixed(6)},{" "}
                          {formData.longitude.toFixed(6)}
                        </p>
                      </div>
                    )}

                  <div className={styles.formActions}>
                    <Button type="submit" loading={loading} disabled={loading}>
                      {editingAddress
                        ? t("my_locations.update_btn")
                        : t("my_locations.save_btn")}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Ünvan siyahısı */}
            {addresses.length > 0 && !showForm && (
              <div className={styles.addressList}>
                {addresses.map((address) => (
                  <div key={address.id} className={styles.addressCard}>
                    <div className={styles.addressHeader}>
                      <h3 className={styles.addressTitle}>
                        {address.city?.name || ""},{" "}
                        {address.city?.country?.name || ""}
                      </h3>
                      <button
                        className={styles.editIcon}
                        onClick={() => handleEdit(address)}
                        disabled={loading}
                      >
                        ✏️
                      </button>
                    </div>
                    <div className={styles.addressDetails}>
                      <p className={styles.addressRow}>
                        <span className={styles.addressLabel}>
                          {t("my_locations.form.country")}
                        </span>
                        <span className={styles.addressValue}>
                          {address.city?.country?.name || ""}
                        </span>
                      </p>
                      <p className={styles.addressRow}>
                        <span className={styles.addressLabel}>
                          {t("my_locations.form.city")}
                        </span>
                        <span className={styles.addressValue}>
                          {address.city?.name || ""}
                        </span>
                      </p>
                      <p className={styles.addressRow}>
                        <span className={styles.addressLabel}>
                          {t("my_locations.form.phone_number")}
                        </span>
                        <span className={styles.addressValue}>
                          {address.phone}
                        </span>
                      </p>
                      <p className={styles.addressRow}>
                        <span className={styles.addressLabel}>
                          {t("my_locations.form.address")}
                        </span>
                        <span className={styles.addressValue}>
                          {address.address}
                        </span>
                      </p>
                      <p className={styles.addressRow}>
                        <span className={styles.addressLabel}>
                          {t("my_locations.form.zip_code")}
                        </span>
                        <span className={styles.addressValue}>
                          {address.zip_code || "-"}
                        </span>
                      </p>
                    </div>
                    <div className={styles.addressActions}>
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleDelete(address.id)}
                        disabled={loading}
                      >
                        {t("my_locations.delete_btn")}
                      </button>
                      <button
                        className={styles.addNewButton}
                        onClick={handleAddNew}
                        disabled={loading}
                      >
                        {t("my_locations.add_new_address")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Addresses;
